<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="nodeSideMenu-createNodes-createActivator">
        <slot></slot>
      </div>
    </template>

    <v-card data-cy="dialog-createNode-modal">
      <v-card-title class="d-flex justify-space-between" data-cy="dialog-createNode-title">
        {{getTitleString}}

        <v-btn icon="mdi-close" @click="dialog = false" :aria-label="$t('common.close')" />
      </v-card-title>

      <v-card-text class="scrollable">
        <v-form ref="form" v-on:submit.prevent="createNode" v-bind:disabled="!currentNode" style="margin-top: 16px;">

          <v-text-field
            v-model="nodeName"
            :placeholder="$t('menus.createNode.nodeName')"
            :rules="nodeNameRules"
            :disabled="isOwnAccessPoint && !$canControlTenantConfig()"
            required
            variant="solo-filled"
            flat
            data-cy="dialog-createNode-nodeName">
          </v-text-field>

          <v-textarea
            v-model="nodeNote"
            :placeholder="$t('menus.createNode.nodeNote')"
            :rules="nodeNoteRules"
            variant="solo-filled"
            flat
            rows="3"
            class="media-list-notes-textarea"
            data-cy="dialog-createNode-nodeNote"
          >
          </v-textarea>

          <div v-if="isClientNode" class="mt-4">
            <v-text-field v-model="clientAddressLine1"
                          :placeholder="$t('menus.createNode.addressLine1')" variant="solo-filled"
                          flat data-cy="dialog-createNode-client-addr1"/>
            <v-text-field v-model="clientAddressLine2"
                          :placeholder="$t('menus.createNode.addressLine2')" variant="solo-filled"
                          flat data-cy="dialog-createNode-client-addr2"/>
            <v-text-field v-model="clientZipCode" :placeholder="$t('menus.createNode.zipCode')"
                          variant="solo-filled" flat
                          data-cy="dialog-createNode-client-zipCode"/>
            <v-text-field v-model="clientCity" :placeholder="$t('menus.createNode.city')"
                          variant="solo-filled" flat
                          data-cy="dialog-createNode-client-city"/>
            <v-select
              v-model="clientCountry"
              v-bind:items="availableCountries"
              item-title="country"
              item-value="country"
              :label="$t('menus.createNode.country')"
              :placeholder="$t('menus.createNode.country')"
              variant="solo-filled"
              flat
              data-cy="dialog-createNode-client-selectCountry"
            />
            <v-text-field v-model="clientWebsite" :placeholder="$t('menus.createNode.websiteURL')"
                          :rules="clientWebsiteRules" variant="solo-filled" flat
                          data-cy="dialog-createNode-client-website"/>

            <div class="mb-2 text-center">
              <v-chip
                v-if="selectedLogoError"
                size="small"
                class="bg-error mb-2"
                closable
                @click:close="selectedLogoError = ''"
              >
                {{selectedLogoError}}
              </v-chip>

              <v-chip
                v-if="selectedLogo"
                size="small"
                class="mb-2"
                data-cy="dialog-createNode-client-logo-name"
                closable
                @click:close="clearSelectedLogo"
              >
                {{selectedLogo.name}}({{ formatSize(selectedLogo.size, 0) }})
              </v-chip>

              <div v-if="getLogoCss" v-bind:style="getLogoCss" data-cy="dialog-createNode-client-logo-showLogo">
                <v-btn variant="text" icon="mdi-close" size="xs-small" v-on:click="removeClientLogo" data-cy="dialog-createNode-client-logo-remove-btn" />
              </div>

              <input type="file" ref="fileInput" v-on:change="fileSelected" style="display: none" accept="image/png, image/gif" data-cy="dialog-createNode-client-logo-inputField">
              <v-btn
                v-if="!selectedLogo"
                v-on:click="selectLogoClick"
                elevation="0"
                block
                color="info"
                data-cy="dialog-createNode-client-logo-selectLogo-btn"
              >
                {{$t('menus.createNode.selectLogo')}}
              </v-btn>

              <div class="font-italic text-left"><v-icon icon="mdi-information-outline" size="small"/> {{$t('menus.createNode.selectLogoInfo')}}</div>
            </div>
          </div>

          <v-checkbox
            v-if="$isTeamUser()"
            v-model="teamonly"
            :label="$t('routes.node.teamonly')"
            hide-details
            class="mt-0"
            data-cy="dialog-createNode-teamOnly"
          />

          <!-- Retention Period Section -->
          <div v-if="showRetentionPeriod" class="retention-section">
            <div class="d-flex align-center">
              <v-switch
                v-model="retentionEnabled"
                :label="$t('menus.createNode.retentionPeriod')"
                color="primary"
                hide-details
                class="mt-0"
                :disabled="isDisabledRetentionPeriodManagement"
                data-cy="dialog-createNode-retentionEnabled"
              />

              <v-tooltip location="bottom" :aria-label="$t('menus.createNode.retentionPeriodInfo')">
                <template v-slot:activator="{ props }">
                  <v-icon
                    v-bind="props"
                    icon="mdi-information-outline"
                    size="small"
                    class="ml-2"
                    color="grey grey-lighten-4"
                  />
                </template>
                <div class="retention-tooltip-content">
                  {{ $t('menus.createNode.retentionPeriodInfo') }}
                </div>
              </v-tooltip>
            </div>

            <div v-if="retentionEnabled" class="mt-4">
              <div class="d-flex align-center" style="gap: 16px;">
                <v-text-field
                  v-model.number="retentionPeriod"
                  :label="$t('menus.createNode.period')"
                  :rules="retentionPeriodRules"
                  type="number"
                  min="1"
                  variant="solo-filled"
                  flat
                  class="flex-grow-1"
                  data-cy="dialog-createNode-retentionPeriod"
                  ref="retentionPeriodField"
                />

                <v-select
                  v-model="retentionPeriodType"
                  :items="retentionPeriodTypes"
                  item-title="label"
                  item-value="value"
                  :label="$t('menus.createNode.retentionPeriodType')"
                  :rules="retentionPeriodTypeRules"
                  variant="solo-filled"
                  flat
                  class="flex-shrink-0"
                  style="min-width: 120px;"
                  data-cy="dialog-createNode-retentionPeriodType"
                />
              </div>
            </div>

            <div v-if="hasParentWithRetention" class="mt-2">
              <div
                size="small"
                data-cy="dialog-createNode-retentionInherited"
                class="font-italic"
              >
                <v-icon icon="mdi-information-outline" size="small"></v-icon>
                {{ $t('menus.createNode.retentionInherited') }}
                {{ $t('menus.createNode.retentionPeriodIs') }} {{ parentRetentionPeriod }}
              </div>
            </div>

            <div v-if="hasChildWithRetention" class="mt-2">
              <div
                size="small"
                data-cy="dialog-createNode-retentionInherited"
                class="font-italic"
              >
                <v-icon icon="mdi-information-outline" size="small"></v-icon>
                {{ $t('menus.createNode.hasChildWithRetention') }}
              </div>
            </div>
          </div>

          <div v-if="!isClientNode && !editNodeId">
            <div class="vb-folder-info-class">
              {{ isFolderNode ? $t('routes.node.newFolderInfo') : $t('routes.node.newProjectInfo') }} <a :href="desitnationNodePath" target="_blank" >{{ desitnationNodeName }}</a>
            </div>
          </div>

          <div v-if="successMessage" class="bg-success" data-cy="dialog-createNode-successMessage">
            {{successMessage}}
          </div>

          <div v-if="errorMessage" class="bg-error" data-cy="dialog-createNode-errorMessage">
            {{errorMessage}}
          </div>

        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="isSubmitButtonDisabled"
          @click="createNode"
          color="primary"
          variant="flat"
          elevation="0"
          data-cy="dialog-createNode-createButton"
        >
          {{getTitleString}}
        </v-btn>
        <v-btn
          variant="text"
          @click="dialog = false"
          data-cy="dialog-createNode-closeButton"
        >
          {{$t('common.cancel')}}
        </v-btn>
      </v-card-actions>
    </v-card>

  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import { useCommonStore } from '@/stores/CommonStore.js';
import AllCountries from './countries.json';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { formatSize } from '@/components/formatters.js';

export default {
  mixins: [DialogActionKeys],
  name: 'CreateNodeDialog',
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    nodeType: {
      type: Number,
      required: true,
    },
    parentNode: {
      type: Object,
      required: false,
    },
    editNodeId: {
      type: String,
      required: false,
    },
  },
  data() {
    const websiteRE = new RegExp('^(https?:\\/\\/)?'+ // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
      '(\\#[-a-z\\d_]*)?$','i'); // fragment locator

    return {
      successMessage: '',   // not used yet
      errorMessage: '',

      nodeName: '',
      nodeNameRules: [
        (v) => !!v || this.$t('menus.createNode.nodeNameRequired'),
        (v) => (v && v.length <= 191) || this.$t('menus.createNode.nodeNameMaxLength'),
      ],

      nodeNote: '',
      nodeNoteRules: [
        (v) => (!v) || (v && v.length <= 4096) || this.$t('menus.createNode.nodeNoteMaxLength'),
      ],

      teamonly: false,
      isSubmitButtonDisabled: false,

      // Retention period fields
      retentionEnabled: false,
      retentionPeriod: null,
      retentionPeriodType: 'hours',
      retentionPeriodTypes: [
        { label: this.$t('menus.createNode.hours'), value: 'hours' },
        { label: this.$t('menus.createNode.days'), value: 'days' },
        { label: this.$t('menus.createNode.weeks'), value: 'weeks' },
        { label: this.$t('menus.createNode.months'), value: 'months' },
      ],
      retentionPeriodTypeRules: [
        (v) => !this.retentionEnabled || !!v || this.$t('menus.createNode.retentionPeriodTypeRequired'),
      ],

      availableCountries: AllCountries,

      clientAddressLine1: '',
      clientAddressLine2: '',
      clientZipCode: '',
      clientCity: '',
      clientCountry: '',
      clientWebsite: '',
      clientWebsiteRules: [
        (v) => (!v || websiteRE.test(v)) || this.$t('menus.createNode.websiteValid'),
      ],
      selectedLogo: null,
      selectedLogoError: '',

      retentionPeriodError: null,
      maxPeriods: {
        hours: 900,
        days: 600,
        weeks: 300,
        months: 60,
      },
    };
  },
  mounted() {
    this.initializeForm();
  },
  watch: {
    dialog(newValue, oldValue) {
      // clean-up the form on close
      if (oldValue) {
        this.$refs.form.reset();

        if (this.isClientNode) {
          this.$refs.fileInput.value = null;
          this.selectedLogo = null;
          this.selectedLogoError = '';
        }

        this.errorMessage = '';
        this.retentionEnabled = false;
        this.retentionPeriod = null;
        this.retentionPeriodType = 'hours';
      }

      // on open get the node info for edit
      if (newValue && this.editNode) {
        const self = this;

        self.isSubmitButtonDisabled = true;
        const nodeStore = useNodeStore();

        nodeStore.getNodeInfo(this.editNode.id)
          .then(() => {
            self.initializeForm();
            self.isSubmitButtonDisabled = false;
          });
      } else if (newValue) {
        // on open prepare the form to create
        this.initializeForm();
        this.errorMessage = '';
        this.successMessage = '';
      }
    },
    retentionPeriod(newValue) {
      this.validateRetentionPeriod(newValue, this.retentionPeriodType);
    },

    retentionPeriodType(newValue) {
      this.validateRetentionPeriod(this.retentionPeriod, newValue);
    },
  },
  methods: {
    validateRetentionPeriod(period, periodType) {
      // Clear any existing error
      this.retentionPeriodError = null;

      // Skip validation if retention is not enabled
      if (!this.retentionEnabled) {
        return;
      }

      // Skip validation if period or periodType is not set
      if (!period || !periodType) {
        return;
      }

      // Convert period to number for comparison
      const numericPeriod = Number(period);

      // Skip validation if period is not a valid number
      if (isNaN(numericPeriod) || numericPeriod <= 0) {
        return;
      }

      // Check if the period type exists in maxPeriods
      if (!Object.prototype.hasOwnProperty.call(this.maxPeriods, periodType)) {
        return;
      }

      // Validate against maximum allowed value
      const maxAllowed = this.maxPeriods[periodType];
      if (numericPeriod > maxAllowed) {
        this.retentionPeriodError = this.$t('menus.createNode.retentionPeriodMax', {
          max: maxAllowed,
          unit: this.$t(`common.${periodType}`) || periodType,
        });
      }

      // Force re-validation of the field
      this.$nextTick(() => {
        // If you have a ref on your v-text-field, you can trigger validation
        this.$refs.retentionPeriodField?.validate();
      });
    },
    formatSize,
    async initializeForm() {
      if (this.editNode) {
        this.nodeName = this.editNode.name;
        this.nodeNote = this.editNode.note;
        this.teamonly = Boolean(this.editNode.teamonly);
        this.selectedNodeType = this.editNode.type;
        this.clientAddressLine1 = this.editNode.clientaddressline1;
        this.clientAddressLine2 = this.editNode.clientaddressline2;
        this.clientZipCode = this.editNode.clientzipcode;
        this.clientCity = this.editNode.clientcity;
        this.clientCountry = this.editNode.clientcountry;
        this.clientWebsite = this.editNode.clientwebsite;

        // Initialize retention period fields for edit
        if (this.editNode.retentionperiod && this.editNode.retentionperiod > 0) {
          this.retentionEnabled = true;
          this.retentionPeriod = this.editNode.retentionperiod;

          if (this?.editNode?.retentiontype) {
            this.retentionPeriodType = this._convertFromDatabaseRetentionType(this.editNode.retentiontype);
          } else {
            this.retentionPeriodType = this._convertFromDatabaseRetentionType('hours');
          }
        } else {
          this.retentionEnabled = false;
          this.retentionPeriod = null;
          this.retentionPeriodType = 'hours';
        }
      } else {
        this.teamonly = (this.$isTeamUser() && this.nodeType === this.nodeTypes.PROJECT);
        this.retentionEnabled = false;
        this.retentionPeriod = null;
        this.retentionPeriodType = 'hours';
      }
    },
    async createNode() {
      if (this.isSubmitButtonDisabled) {
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      const { valid } = await this.$refs.form.validate();
      if (!valid || !this.currentNode) {
        return;
      }

      if (!this.editNode && !this.parentNode) {
        return;
      }

      const formData = {
        name: this.nodeName,
        note: this.nodeNote || '',
        teamonly: Boolean(this.teamonly),
        parentid: this.editNode ? this.editNode.parentid : this.parentNode.id,
        type: this.nodeType,
      };

      // Add retention period data if enabled
      if (this.retentionEnabled && this.retentionPeriod && this.retentionPeriodType) {
        formData.retentionperiod = this.retentionPeriod;
        formData.retentionperiodtype = this.retentionPeriodType;
      }

      if (this.isClientNode) {
        const authStore = useAuthStore();

        formData.clientaddressline1 = this.clientAddressLine1 || '';
        formData.clientaddressline2 = this.clientAddressLine2 || '';
        formData.clientzipcode = this.clientZipCode || '';
        formData.clientcity = this.clientCity || '';
        formData.clientcountry = this.clientCountry || '';
        formData.clientwebsite = this.clientWebsite || '';
        formData.logo = this.selectedLogo ? this.selectedLogo : null;
        formData.parentid = authStore.rootNodeId;
      }

      if (this.editNode) {
        formData.id = this.editNode.id;
      }

      const self = this;
      const nodeStore = useNodeStore();

      let VERB = this.editNode
        ? nodeStore.updateNode
        : nodeStore.createNode;

      if (this.editNode && (this.editNode.type === this.nodeTypes.FOLDER)) {
        VERB = nodeStore.updateFolder;
      }

      this.isSubmitButtonDisabled = true;
      VERB(formData)
        .then((result) => {
          self.dialog = false;
          self.isSubmitButtonDisabled = false;

          const routePath = `/node/${result.id}`;
          if (!self.editNode && (self.$route.path !== routePath)) {
            self.$router.push(routePath);
          }

          if (self.editNode) {
            self.$showMessage(self.$t('menus.createNode.successMessageUpdate'));
          }
        })
        .catch((error) => {
          self.isSubmitButtonDisabled = false;
          const errorMessage = error?.response?.data?.error?.message;
          if (errorMessage) {
            self.errorMessage = self.$replaceT(
              error.response.data.error.code,
              null,
              errorMessage,
            );
          } else if (error && error.response && error.response.data && error.response.data.error) {
            self.errorMessage = error.response.data.error.message;
          } else {
            if (error?.name === 'AxiosError') {
              console.warn(`AxiosError: ${error?.message}`);
            } else {
              // unhandled error
              self.errorMessage = 'System Error!';
              console.error(error);
            }
          }
        });
    },
    selectLogoClick() {
      this.$refs.fileInput.click();
    },
    async fileSelected(e) {
      this.selectedLogo = null;
      this.selectedLogoError = '';

      if (e.target.files && e.target.files[0]) {
        if (e.target.files[0].size > 2097152) {
          this.selectedLogoError = this.$t('menus.createNode.theLogoIsTooLarge');
        } else {
          const [width, height] = await this.getLogoDimensions(e.target.files[0]);
          if (width < 0 || height < 0 || width > 512 || height > 512) {
            this.selectedLogoError = this.$t('menus.createNode.theLogoIsTooLarge');
          } else {
            this.selectedLogo = e.target.files[0];
          }
        }
      }

      this.$refs.fileInput.value = null;
    },
    clearSelectedLogo() {
      this.selectedLogo = null;
      this.selectedLogoError = '';
    },
    removeClientLogo() {
      const nodeStore = useNodeStore();
      nodeStore.deleteLogo(this.editNode.id);
    },
    async getLogoDimensions(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('error', () => resolve([-1, -1]));
        reader.addEventListener('load', () => {
          const image = new Image();
          image.addEventListener('load', () => {
            resolve([image.width, image.height]);
          });
          image.src = reader.result;
        });
        reader.readAsDataURL(file);
      });
    },
    /**
     * Convert database retention type enum to frontend format
     * @param {string} dbType - Database enum value (HOURS, DAYS, WEEKS, MONTHS)
     * @returns {string} Frontend format (hours, days, weeks, months)
     */
    _convertFromDatabaseRetentionType(dbType) {
      const typeMap = {
        'HOURS': 'hours',
        'DAYS': 'days',
        'WEEKS': 'weeks',
        'MONTHS': 'months',
      };

      return typeMap[dbType] || 'hours';
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    desitnationNodeName() {
      if (this.parentNode) {
        return this.parentNode.name;
      }

      if (this.currentNode) {
        return this.currentNode.name;
      }

      return '';
    },
    desitnationNodePath() {
      if (this.parentNode && this.parentNode.id) {
        return `/node/${this.parentNode.id}`;
      }

      if (this.currentNode && this.currentNode.id) {
        return `/node/${this.currentNode.id}`;
      }

      return '#';
    },
    editNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getNodeById(this.editNodeId);
    },
    nodeTypes() {
      const commonStore = useCommonStore();
      return commonStore.nodeTypes;
    },
    isClientNode() {
      return this.nodeType === this.nodeTypes.CLIENT;
    },
    isProjectNode() {
      return this.nodeType === this.nodeTypes.PROJECT;
    },
    isFolderNode() {
      return this.nodeType === this.nodeTypes.FOLDER;
    },
    showRetentionPeriod() {
      return this.isFolderNode || this.isProjectNode || this.isClientNode;
    },
    getTitleString() {
      if (this.editNode) {
        if (this.isClientNode) return this.$t('menus.createNode.updateClient');
        if (this.isProjectNode) return this.$t('menus.createNode.updateProject');
        return this.$t('menus.createNode.updateFolder');
      }

      if (this.isClientNode) return this.$t('menus.createNode.createClient');
      if (this.isProjectNode) return this.$t('menus.createNode.createProject');
      return this.$t('menus.createNode.createFolder');
    },
    getLogoCss() {
      if (this.editNode && this.editNode.clientlogourl) {
        return {
          width: '150px',
          height: '150px',
          margin: 'auto',
          marginBottom: '12px',
          textAlign: 'right',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundImage: `url("${this.editNode.clientlogourl}")`,
        };
      }

      return false;
    },
    isOwnAccessPoint() {
      const authStore = useAuthStore();
      const accessPointIds = authStore.getAccessPointIds;

      if (this.editNode) {
        const nodeId = this.editNode.id;
        return accessPointIds.includes(nodeId);
      }

      return false;
    },
    isDisabledRetentionPeriodManagement() {
      // Check if the node that will be edited has parent with retention period
      if (this.editNode && (this.editNode?.retention?.hasParentWithRetention || this.editNode?.retention?.hasChildWithRetention)) {
        return true;
      }

      if (this.isClientNode) {
        return false;
      }

      // If there is parent node, that means we are creating a new node under it.
      if (this.parentNode) {
        // Check if the parent node, its parents or its children have retention period.
        // If so disable the option for setting retention period
        if (this.parentNode?.retention?.hasRetention || this.parentNode?.retention?.hasParentWithRetention) {
          return true;
        }
      }

      return false;
    },
    hasParentWithRetention() {
      if (this.isClientNode) {
        return false;
      }

      // Check if the node that will be edited has parent with retention period
      if (this.editNode && this.editNode?.retention?.hasParentWithRetention) {
        return true;
      }

      // If there is parent node, that means we are creating a new node under it.
      if (this.parentNode) {
        // Check if the parent node and its parents have retention period.
        // If so show a message that there is parent with retention period
        if (this.parentNode?.retention?.hasRetention || this.parentNode?.retention?.hasParentWithRetention) {
          return true;
        }
      }

      return false;
    },
    hasChildWithRetention() {
      // Check if the node that will be edited has child with retention period
      return !!(this.editNode && this.editNode?.retention?.hasChildWithRetention);
    },
    parentRetentionPeriod() {
      if (this.isClientNode) {
        return '';
      }

      if (this.parentNode
        && this.parentNode.retention
        && (this.parentNode.retention.hasRetention || this.parentNode.retention.hasParentWithRetention)
      ) {
        if (this.parentNode.retention.hasRetention) {
          return this.parentNode.retentionperiod + ' ' + this.$t(`common.${this.parentNode.retentiontype.toLowerCase()}`, this.parentNode.retentionperiod || 0) + '.';
        }

        if (this.parentNode.retention.hasParentWithRetention) {
          return this.parentNode.retention.period + ' ' + this.$t(`common.${this.parentNode.retention.type.toLowerCase()}`, this.parentNode.retention.period || 0) + '.';
        }
      }

      if (this?.editNode?.retention?.hasParentWithRetention) {
        return this.editNode.retention.period + ' ' + this.$t(`common.${this.editNode.retention.type.toLowerCase()}`, this.editNode.retention.period || 0) + '.';
      }

      console.log('this.parentNode', this.parentNode);

      return '';
    },
    retentionPeriodRules() {
      const baseRules = [
        (v) => !this.retentionEnabled || !!v || this.$t('menus.createNode.retentionPeriodRequired'),
        (v) => !this.retentionEnabled || (v && Number(v) > 0) || this.$t('menus.createNode.retentionPeriodMin'),
        (v) => !this.retentionEnabled || (v && Number.isInteger(Number(v))) || this.$t('menus.createNode.retentionPeriodInteger'),
      ];

      // Add custom error from watcher if it exists
      if (this.retentionPeriodError) {
        baseRules.push(() => this.retentionPeriodError);
      }

      return baseRules;
    },
  },
};
</script>

<style>
.vb-folder-info-class {
  margin-top: 10px;
  padding: 10px 0 10px 5px;
  font-style: italic;
}

.retention-section {
  padding-left: 8px;
}

.retention-tooltip-content {
  max-width: 300px;
  white-space: normal;
  font-size: 14px;
  line-height: 1.4;
}
</style>
